import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { Environment, EnvironmentService } from '@galaxy/core';
import { EmailStatsModule } from '@galaxy/email-ui/email-activity';
import {
  AdvancedSections,
  DYNAMIC_COMPONENT_DATA_TOKEN,
  EMAIL_FOOTER_TOKEN,
  EmailBuilderModule,
  EmailContentData,
  EmailPreviewHydrationData,
  HasUnsavedChangesGuard,
  Footer,
} from '@galaxy/email-ui/email-builder';
import {
  DynamicComponentSelectorModule,
  VariableMenuItem,
} from '@galaxy/email-ui/email-builder/src/components/dynamic-component-selector';
import { EmailBuilderDataService } from '@galaxy/email-ui/email-builder/src/services/email-builder-data.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AccountGroupApiService, ListRequest, PagedResponse } from '@vendasta/account-group';
import { AppNamespace, OwnerType } from '@vendasta/email-builder';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyStatisticModule } from '@vendasta/galaxy/statistic';
import { AsyncUiModule } from '@vendasta/uikit';
import { catchError, combineLatest, EMPTY, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { AUTOMATION_CONTEXT_INJECTION_TOKEN, AUTOMATION_PARTNER_ID_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { Context } from '@vendasta/automata';

interface AutomationData {
  ownerId: string;
  marketId: string;
  isNew: boolean;
  emailId: string;
}

@Component({
  templateUrl: './email-editor-dialog.component.html',
  styleUrls: ['./email-editor-dialog.component.scss'],
  imports: [
    CommonModule,
    EmailBuilderModule,
    GalaxyInputModule,
    GalaxyPopoverModule,
    GalaxyAlertModule,
    GalaxyPipesModule,
    GalaxyPageModule,
    GalaxyStatisticModule,
    FormsModule,
    GalaxyFormFieldModule,
    MatDividerModule,
    DynamicComponentSelectorModule,
    // ContentEditorModule,
    AsyncUiModule,
    MatMenuModule,
    // BaseTemplateEditingModule,
    GalaxyLoadingSpinnerModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    // PreviewBusinessSelectorModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    EmailStatsModule,
  ],
})
export class EmailEditorDialogComponent implements OnInit {
  isNew: boolean = this.automationData.isNew;

  templateId: string | undefined = undefined;
  templateData$: Observable<EmailContentData> = EMPTY;
  private dataForSaving: EmailContentData;
  private rawHtmlForSaving: string;
  htmlRendering = false;

  readonly previewHydrationData$: Observable<EmailPreviewHydrationData>;
  private readonly selectedBusiness$: Subject<any> = new Subject<any>();

  readonly advancedSections: AdvancedSections = {
    topLogo: true,
    pageSetup: true,
    footer: true,
    colors: true,
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public automationData: AutomationData,
    private readonly dialogRef: MatDialogRef<EmailEditorDialogComponent>,
    private readonly dataService: EmailBuilderDataService,
    public environmentService: EnvironmentService,
    public accountGroups: AccountGroupApiService,
    private readonly changesGuard: HasUnsavedChangesGuard,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    @Inject(DYNAMIC_COMPONENT_DATA_TOKEN) readonly dynamicComponents: VariableMenuItem[],
    @Inject(AUTOMATION_PARTNER_ID_INJECTION_TOKEN$) private readonly partnerId$: Observable<string>,
    @Inject(AUTOMATION_CONTEXT_INJECTION_TOKEN) private readonly context: Context,
    @Inject(EMAIL_FOOTER_TOKEN) @Optional() private readonly footer$: Observable<Footer>,
  ) {
    const initialPreviewHydrationData$ = this.buildHydrationData(automationData.marketId);
    this.previewHydrationData$ = combineLatest([
      initialPreviewHydrationData$,
      this.selectedBusiness$.pipe(startWith(undefined)),
    ]).pipe(
      map(([initial, selected]: [EmailPreviewHydrationData, any]) => {
        const output = Object.assign({}, initial);
        if (selected) {
          output.business = selected;
        }
        return output;
      }),
    );
  }

  ngOnInit(): void {
    this.templateId = this.automationData.emailId;
    if (this.isNew) {
      const footer$ = this.footer$
        ? this.footer$
        : of({
            collapsed: true,
          } as Footer);
      this.templateData$ = footer$.pipe(
        map((footer) => {
          const initialContent: EmailContentData = {
            subjectLine: '',
            previewText: '',
            id: this.templateId,
            emailSetup: {
              pageSetup: {
                collapsed: true,
              },
              topLogo: {
                showLogo: 'show',
                collapsed: true,
              },
              colors: {
                collapsed: true,
              },
              footer: footer,
            },
            emailContentBlocks: [],
          };
          return initialContent;
        }),
      );
    } else {
      this.templateData$ = this.loadTemplate();
    }
  }

  loadTemplate(): Observable<EmailContentData> {
    return this.dataService.loadTemplate$(
      this.automationContextToOwnerType(this.context),
      this.automationData.ownerId,
      AppNamespace.AUTOMATIONS,
      this.templateId,
    );
  }

  setHtmlRendering($event: boolean): void {
    this.htmlRendering = $event;
  }

  stageDataForSaving($event: EmailContentData): void {
    this.dataForSaving = $event;
  }

  stageRawHtmlForSaving($event: string): void {
    this.rawHtmlForSaving = $event;
  }

  async saveAndClose(): Promise<void> {
    const success = await firstValueFrom(this.save());
    if (!success) {
      return;
    }
    this.closeDialog(this.dataForSaving?.name, this.dataForSaving?.subjectLine);
  }

  closeDialog(templateName?: string, templateSubject?: string): void {
    if (this.changesGuard.canNavigateAway) {
      this.dialogRef.close([templateName, templateSubject]);
    } else {
      const dialog = this.changesGuard.showUnsavedChangesDialog();
      dialog.afterClosed().subscribe((result) => {
        if (result?.navigateAwayAndDiscard) {
          this.dialogRef.close([templateName, templateSubject]);
        }
      });
    }
  }

  save(): Observable<boolean> {
    const obs = this.isNew ? this.sendCreateRequest() : this.sendUpdateRequest();
    return obs.pipe(
      tap((success) => {
        if (!success) {
          this.snackbarService.openErrorSnack(this.translate.instant('EMAIL_BUILDER.SAVING.GENERIC_ERROR'));
          return;
        }
        this.changesGuard.markAsSaved();
      }),
      catchError((err) => {
        this.snackbarService.openErrorSnack(this.translate.instant('EMAIL_BUILDER.SAVING.GENERIC_ERROR'));
        throw new Error(err);
      }),
    );
  }

  private sendUpdateRequest(): Observable<boolean> {
    return this.dataService.updateExistingTemplate$(
      this.automationContextToOwnerType(this.context),
      this.automationData.ownerId,
      AppNamespace.AUTOMATIONS,
      this.templateId,
      this.dataForSaving,
      this.rawHtmlForSaving,
    );
  }

  private sendCreateRequest(): Observable<boolean> {
    return this.dataService.createNewTemplate$(
      this.automationContextToOwnerType(this.context),
      this.automationData.ownerId,
      AppNamespace.AUTOMATIONS,
      this.templateId,
      this.dataForSaving,
      this.rawHtmlForSaving,
    );
  }

  buildHydrationData(marketId: string): Observable<EmailPreviewHydrationData> {
    const accountGroup$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.accountGroups.list(
          new ListRequest({
            pageSize: 1,
            filters: {
              partnerId,
            },
            projectionFilter: {
              napData: true,
              snapshotReports: true,
              accountGroupExternalIdentifiers: true,
            },
          }),
        );
      }),
      map((v: PagedResponse) => (v.accountGroups || [])[0]),
    );

    return accountGroup$.pipe(
      map((accountGroup) => ({
        business: {
          accountGroupId: accountGroup.accountGroupId,
          name: accountGroup.napData?.companyName,
          hasSnapshot: accountGroup.snapshotReports?.snapshots?.length > 0,
          hasSalesperson: !!accountGroup.accountGroupExternalIdentifiers.salesPersonId,
        },
        userID: this.getExampleUserId(this.environmentService.getEnvironment()),
        // contactID is needed to render CRM dynamic content
        contactID: 'fakeContactId',
        partnerID: accountGroup.accountGroupExternalIdentifiers.partnerId,
        marketID: marketId,
        locale: 'en',
      })),
    );
  }

  getExampleUserId(env: Environment): string {
    let userId = 'U-862a9c0e-e46d-42ca-9f1b-21303a52ca9c';
    if (env === Environment.PROD) {
      userId = 'U-4c8b64a1-4120-40b6-82d2-f2e0daa2fcc1';
    }
    return userId;
  }

  automationContextToOwnerType(context: Context): OwnerType {
    switch (context) {
      case Context.AUTOMATION_CONTEXT_PARTNER:
        return OwnerType.OWNER_TYPE_PARTNER;
      case Context.AUTOMATION_CONTEXT_SMB:
        return OwnerType.OWNER_TYPE_ACCOUNT_GROUP;
      default:
        return OwnerType.OWNER_TYPE_PARTNER;
    }
  }
}
